<template>
  <el-dialog
    v-model="visible"
    title="订单详情"
    width="60%"
    :before-close="handleClose"
  >
    <div class="order-detail">
      <!-- 订单基础信息 -->
      <el-card class="detail-card" shadow="never">
        <template #header>
          <div class="card-header">
            <span>订单基础信息</span>
          </div>
        </template>
        <el-descriptions :column="2" border>
          <el-descriptions-item label="订单编号">{{ orderData.id || '无' }}</el-descriptions-item>
          <el-descriptions-item label="订单名称">{{ orderData.name || '无' }}</el-descriptions-item>
          <el-descriptions-item label="订单状态">
            <el-tag>{{ getStatusLabel(orderData.status) }}</el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="创建时间">{{ orderData.createTime || '无' }}</el-descriptions-item>
          <el-descriptions-item label="用户ID">{{ orderData.userId || '无' }}</el-descriptions-item>
        </el-descriptions>
      </el-card>

      <!-- 收货地址信息 -->
      <el-card class="detail-card" shadow="never">
        <template #header>
          <div class="card-header">
            <span>收货地址信息</span>
          </div>
        </template>
        <el-descriptions :column="2" border>
          <el-descriptions-item label="收货人">{{ orderData.postinfo?.name || '无' }}</el-descriptions-item>
          <el-descriptions-item label="联系电话">{{ orderData.postinfo?.phone || '无' }}</el-descriptions-item>
          <el-descriptions-item label="收货地址" :span="2">{{ orderData.postinfo?.address || '无' }}</el-descriptions-item>
        </el-descriptions>
      </el-card>

      <!-- 价格信息 -->
      <el-card class="detail-card" shadow="never">
        <template #header>
          <div class="card-header">
            <span>价格信息</span>
          </div>
        </template>
        <el-descriptions :column="3" border>
          <el-descriptions-item label="打印费用">{{ orderData.printPrice || '0' }}元</el-descriptions-item>
          <el-descriptions-item label="邮寄费用">{{ orderData.postPrice || '0' }}元</el-descriptions-item>
          <el-descriptions-item label="订单总价">{{ orderData.totalPrice || '0' }}元</el-descriptions-item>
          <el-descriptions-item label="优惠金额" v-if="orderData.discountPrice">{{ orderData.discountPrice }}元</el-descriptions-item>
          <el-descriptions-item label="实际支付" v-if="orderData.realPrice">{{ orderData.realPrice }}元</el-descriptions-item>
        </el-descriptions>
      </el-card>

      <!-- 物流信息 -->
      <el-card class="detail-card" shadow="never" v-if="orderData.realNo || orderData.postcode">
        <template #header>
          <div class="card-header">
            <span>物流信息</span>
          </div>
        </template>
        <el-descriptions :column="2" border>
          <el-descriptions-item label="真实订单号" v-if="orderData.realNo">{{ orderData.realNo }}</el-descriptions-item>
          <el-descriptions-item label="快递单号" v-if="orderData.postcode">{{ orderData.postcode }}</el-descriptions-item>
        </el-descriptions>
      </el-card>

      <!-- 售后信息 -->
      <el-card class="detail-card" shadow="never" v-if="orderData.reason || orderData.backPrice || orderData.processResult">
        <template #header>
          <div class="card-header">
            <span>售后信息</span>
          </div>
        </template>
        <el-descriptions :column="2" border>
          <el-descriptions-item label="售后原因" v-if="orderData.reason" :span="2">{{ orderData.reason }}</el-descriptions-item>
          <el-descriptions-item label="退款金额" v-if="orderData.backPrice">{{ orderData.backPrice }}元</el-descriptions-item>
          <el-descriptions-item label="处理结果" v-if="orderData.processResult">{{ orderData.processResult }}</el-descriptions-item>
        </el-descriptions>
      </el-card>

      <!-- 优惠券信息 -->
      <el-card class="detail-card" shadow="never" v-if="orderData.coupon">
        <template #header>
          <div class="card-header">
            <span>优惠券信息</span>
          </div>
        </template>
        <el-descriptions :column="2" border>
          <el-descriptions-item label="优惠券名称">{{ orderData.coupon.name || '无' }}</el-descriptions-item>
          <el-descriptions-item label="优惠券面额">{{ orderData.coupon.price || '0' }}元</el-descriptions-item>
          <el-descriptions-item label="使用门槛">满{{ orderData.coupon.minPrice || '0' }}元可用</el-descriptions-item>
          <el-descriptions-item label="有效期至">{{ orderData.coupon.expireAt || '无' }}</el-descriptions-item>
        </el-descriptions>
      </el-card>

      <!-- 打印文件信息 -->
      <el-card class="detail-card" shadow="never">
        <template #header>
          <div class="card-header">
            <span>打印文件信息</span>
            <el-tag type="info">共 {{ orderData.prints?.length || 0 }} 个文件</el-tag>
          </div>
        </template>
        <el-table :data="orderData.prints" border style="width: 100%">
          <el-table-column prop="name" label="文件名" min-width="150" />
          <el-table-column prop="ext" label="格式" width="80">
            <template #default="scope">
              <el-tag size="small">{{ scope.row.ext?.toUpperCase() || '未知' }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="pageCount" label="页数" width="80" />
          <el-table-column prop="fileSize" label="文件大小" width="100">
            <template #default="scope">
              {{ scope.row.fileSize ? `${(scope.row.fileSize / 1024).toFixed(2)}KB` : '未知' }}
            </template>
          </el-table-column>
          <el-table-column prop="price" label="打印费用" width="100">
            <template #default="scope">
              {{ scope.row.price || '0' }}元
            </template>
          </el-table-column>
          <el-table-column label="打印配置" min-width="200">
            <template #default="scope">
              <div v-if="scope.row.meta" class="print-config">
                <el-tag size="small" v-if="scope.row.meta.name" class="config-tag">{{ scope.row.meta.name }}</el-tag>
                <el-tag size="small" v-if="scope.row.meta.color" class="config-tag">{{ scope.row.meta.color }}</el-tag>
                <el-tag size="small" v-if="scope.row.meta.type" class="config-tag">{{ scope.row.meta.type }}</el-tag>
                <el-tag size="small" v-if="scope.row.meta.zdType" class="config-tag">{{ scope.row.meta.zdType }}</el-tag>
                <el-tag size="small" v-if="scope.row.meta.count" class="config-tag">{{ scope.row.meta.count }}份</el-tag>
                <el-tag size="small" v-if="scope.row.meta.range" class="config-tag">
                  {{ scope.row.meta.range.join('-') }}页
                </el-tag>
              </div>
              <span v-else class="no-config">无配置信息</span>
            </template>
          </el-table-column>
        </el-table>
      </el-card>
    </div>

    <template #footer>
      <div class="dialog-footer">
        <el-button @click="handleClose">关闭</el-button>
      </div>
    </template>
  </el-dialog>
</template>

<script lang="ts" setup>
import { ref, watch } from 'vue';
import { getStatusLabel } from './docgenerator';

const props = defineProps<{
  modelValue: boolean;
  orderData: any;
}>();

const emit = defineEmits<{
  'update:modelValue': [value: boolean];
}>();

const visible = ref(false);

// 监听props变化
watch(() => props.modelValue, (newVal) => {
  visible.value = newVal;
}, { immediate: true });

// 监听visible变化
watch(visible, (newVal) => {
  emit('update:modelValue', newVal);
});

const handleClose = () => {
  visible.value = false;
};
</script>

<style lang="scss" scoped>
.order-detail {
  .detail-card {
    margin-bottom: 20px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  .card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-weight: bold;
  }

  .print-config {
    .config-tag {
      margin-right: 5px;
      margin-bottom: 3px;
    }
  }

  .no-config {
    color: #999;
    font-size: 12px;
  }
}

.dialog-footer {
  text-align: right;
}
</style>
